import { useState, useEffect, useCallback, useRef } from "react";
import p13nService from "@plannel/services/system/p13n-service";
import debug from "@plannel/utils/debug-util";

/*
 * DW 대시보드 사용자별 개인화(z_p13n) 배선 — 페이지마다 반복되던 ~68줄을 캡슐화.
 *
 * 규약 (스킬 dw-dashboard 와 동일)
 *  - pageCd = viewName (메뉴의 reduxKey 와도 같은 문자열)
 *  - configObj 는 **상위 키 단위 병합**(shallow merge)이라 chart / topN 를 각각 upsert 해도 서로 안 덮음
 *  - 저장은 낙관적 반영(먼저 setState → 그다음 upsert) — 범례 체크박스와 설정 모달이 즉시 동기화
 */

export const DEFAULT_TOP_N = 8;
export const MAX_TOP_N = 10;

export const clampTopN = (n, dflt = DEFAULT_TOP_N) =>
  Math.min(MAX_TOP_N, Math.max(1, Math.round(Number(n) || dflt)));

// 저장값 정규화 — 구(舊) flat 문자열("BAR") 하위호환 → { chartType, activeFlg }
export const normalizeChartConfig = (raw, defaults) => {
  const out = { ...defaults };
  Object.entries(raw ?? {}).forEach(([k, v]) => {
    out[k] = typeof v === "string" ? { ...out[k], chartType: v } : { ...out[k], ...v };
  });
  return out;
};

// 저장된 layout 정규화 — order 는 defaultLayout.order 기준으로 검증(모르는 key 제거, 빠진 key 는 제자리에 보충)
//   hidden/size 는 상위 키로 함께 보존(홈 카드 크기 개인화 등). InventoryDashboard 등 size 미사용 페이지엔 무해.
export const normalizeLayout = (raw, defaults) => {
  const base = defaults ?? { order: [], hidden: {} };
  const valid = new Set(base.order);
  const savedOrder = Array.isArray(raw?.order) ? raw.order.filter((k) => valid.has(k)) : [];
  /*
    저장된 order 에 없던 키(= 새로 추가된 카드)는 뒤에 몰아넣지 않고 기본 순서상의 제자리에 끼운다.
    끝에 붙이면 이미 한 번이라도 개인화한 사용자에겐 새 카드가 늘 화면 맨 아래로 가서,
    카탈로그에서 정한 위치가 그 사람들에게만 의미를 잃는다.
    자리는 "기본 순서에서 바로 앞에 있으면서 저장된 순서에도 남아 있는 카드" 뒤로 잡는다.
  */
  const order = [...savedOrder];
  base.order.forEach((key, i) => {
    if (order.includes(key)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const prev = order.indexOf(base.order[j]);
      if (prev >= 0) { at = prev + 1; break; }
    }
    order.splice(at, 0, key);
  });
  /*
    mode / section 은 카드가 아니라 **지면 전체**에 관한 값이라 order/hidden/size 와 성격이 다르다.
      mode    벤토 격자 ↔ 신문 지면
      section 신문 지면에서 보고 있는 면(모듈)
    여기서 명시적으로 실어 보내지 않으면 저장은 되는데 읽을 때 조용히 사라져서,
    새로고침마다 기본값으로 돌아간다. 다른 대시보드는 이 둘을 저장하지 않으므로 영향이 없다.
    ⚠️ 홈에 지면 단위 설정을 새로 추가하면 **여기에도 반드시 추가할 것.**
  */
  return {
    order,
    hidden: { ...(raw?.hidden ?? {}) },
    size: { ...(raw?.size ?? {}) },
    ...(raw?.mode ? { mode: raw.mode } : {}),
    ...(raw?.section ? { section: raw.section } : {}),
  };
};

/**
 * @param viewName            p13n pageCd (= 페이지 viewName)
 * @param defaultChartConfig  시리즈별 기본 { chartType, activeFlg }
 * @param defaultTopN         상위 N 기본값(미사용 페이지는 무시하면 됨)
 * @param defaultLayout       카드 레이아웃 기본 { order:[cardKey], hidden:{key:bool} }(미사용 페이지는 무시)
 * @returns { chartConfig, topN, layout, loaded, loadFailed, saveChartConfig, saveTopN, saveLayout, toggleSeries }
 *   loaded     — 초기 GET 완료 여부(실패해도 true). 저장값에 따라 **배치가 달라지는** 화면은
 *                이게 true 가 될 때까지 그리지 말 것(기본값으로 한 번 그렸다 다시 그리는 번쩍임 방지).
 *   loadFailed — 초기 GET 실패. 이때 화면이 든 값은 저장값이 아니라 기본값이므로
 *                **저장 함수들은 아무 것도 하지 않는다**(서버의 설정을 기본값으로 덮지 않기 위해).
 *                호출부는 이 값으로 설정 컨트롤을 잠그고 이유를 알릴 것.
 */
const useDashboardP13n = (viewName, { defaultChartConfig = {}, defaultTopN = DEFAULT_TOP_N, defaultLayout } = {}) => {
  const [chartConfig, setChartConfig] = useState(defaultChartConfig);
  const [topN, setTopN] = useState(defaultTopN);
  const [layout, setLayout] = useState(defaultLayout ?? { order: [], hidden: {} });
  /*
    초기 GET 이 끝났는지. 저장값이 도착하기 전에 화면을 그리면 기본값으로 한 번 그렸다가
    저장값으로 다시 그리게 된다 — 배치 자체가 바뀌는 페이지(홈의 벤토↔신문)에서는 그게
    레이아웃이 번쩍이는 것으로 보이고, 카드가 통째로 리마운트돼 조회도 두 번 돈다.
    실패해도 true 로 둔다(응답을 영원히 기다리며 빈 화면에 머무르지 않게).
  */
  const [loaded, setLoaded] = useState(false);
  /*
    초기 GET 이 **실패**했는가. loaded 와 따로 둔다 — 실패해도 화면은 그려야 하지만(빈 화면 방지),
    그때 화면이 들고 있는 건 저장값이 아니라 **기본값**이다.

    🚨 이 상태에서 저장하면 안 된다. 서버에 멀쩡히 있는 순서·크기·모드를 기본값으로 덮어쓴다.
      (실제로 봤다: 백엔드가 내려간 채 홈을 열면 신문 지면이 벤토로 보이는데, 그때 토글을 누르면
       저장돼 있던 레이아웃이 통째로 날아간다.)
      그래서 아래 저장 함수들은 이 플래그가 서 있으면 **서버에 쓰지 않는다.**
  */
  const [loadFailed, setLoadFailed] = useState(false);
  const failedRef = useRef(false);
  const dirtyRef = useRef(false);   // 사용자 저장이 한 번이라도 있었으면 true → 늦게 도착한 초기 GET 이 저장값을 덮지 못하게(랜덤 덮어쓰기 방지)

  useEffect(() => {
    let alive = true;
    p13nService.get(viewName)
      .then((r) => {
        if (!alive || dirtyRef.current) return;   // 이미 저장됨 → stale 응답 무시
        setChartConfig(normalizeChartConfig(r?.data?.chart, defaultChartConfig));
        if (r?.data?.topN != null) setTopN(clampTopN(r.data.topN, defaultTopN));
        if (defaultLayout) setLayout(normalizeLayout(r?.data?.layout, defaultLayout));
      })
      .catch((e) => {
        debug(e);
        failedRef.current = true;
        if (alive) setLoadFailed(true);
      })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewName]);

  /*
    저장 전 관문. 조회가 실패했으면 서버에 쓰지 않는다(위 주석).
    로컬 state 도 바꾸지 않는다 — 화면만 바뀌면 사용자는 저장된 줄 알고 창을 닫는다.
    호출부는 loadFailed 로 컨트롤을 잠그거나 안내를 띄울 것.
  */
  const canPersist = () => {
    if (failedRef.current) {
      debug("[p13n] 초기 조회 실패 상태 — 기본값으로 덮어쓰지 않도록 저장을 건너뜀");
      return false;
    }
    return true;
  };

  // chart 저장(낙관적) — 별도 상위 키라 topN 을 덮지 않음
  const saveChartConfig = useCallback((cfg) => {
    if (!canPersist()) return;
    dirtyRef.current = true;
    setChartConfig(cfg);
    p13nService.upsert({ pageCd: viewName, configObj: { chart: cfg } }).catch((e) => debug(e));
  }, [viewName]);

  // topN 저장(낙관적, 1~10 클램프)
  const saveTopN = useCallback((n) => {
    if (!canPersist()) return;
    const v = clampTopN(n, defaultTopN);
    dirtyRef.current = true;
    setTopN(v);
    p13nService.upsert({ pageCd: viewName, configObj: { topN: v } }).catch((e) => debug(e));
  }, [viewName, defaultTopN]);

  // 범례 체크박스 = 해당 시리즈 activeFlg 반전(설정 모달과 동일 소스, 즉시 저장)
  const toggleSeries = useCallback((key) => {
    if (!canPersist()) return;
    dirtyRef.current = true;
    setChartConfig((prev) => {
      const cur = prev?.[key] ?? {};
      const next = { ...prev, [key]: { ...cur, activeFlg: !(cur.activeFlg !== false) } };
      p13nService.upsert({ pageCd: viewName, configObj: { chart: next } }).catch((e) => debug(e));
      return next;
    });
  }, [viewName]);

  // layout 저장(낙관적) — 별도 상위 키라 chart/topN 을 덮지 않음
  const saveLayout = useCallback((next) => {
    if (!canPersist()) return;
    dirtyRef.current = true;
    setLayout(next);
    p13nService.upsert({ pageCd: viewName, configObj: { layout: next } }).catch((e) => debug(e));
  }, [viewName]);

  return { chartConfig, topN, layout, loaded, loadFailed, saveChartConfig, saveTopN, saveLayout, toggleSeries };
};

export default useDashboardP13n;
